import { useState, useEffect, useCallback, useRef } from 'react';
import { isAuthenticated, getUserId, refreshToken, getAccessToken } from '../services/authService';

interface AuthState {
  isAuthenticated: boolean;
  userId: string | null;
  isLoading: boolean;
}

// Verification tracker to prevent duplicate calls
let lastVerificationTime = 0;
const VERIFICATION_COOLDOWN = 500; // Reduced from 2000ms to 500ms for faster response

// Local cache for faster checks
let authCache = {
  isAuthenticated: false,
  userId: null as string | null,
  timestamp: 0
};

/**
 * Hook for checking authentication status
 * Provides an isAuthenticated state that updates on token changes
 */
export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    userId: null,
    isLoading: true,
  });
  
  // Use a ref to track if a verification is in progress
  const verifyingRef = useRef(false);

  // Check authentication status with debounce
  const checkAuthStatus = useCallback(async () => {
    // Fast path - check if token exists first
    const token = getAccessToken();
    const userId = getUserId();
    
    // If token exists and we have a local cache less than 2 seconds old, use it
    const now = Date.now();
    if (token && userId && (now - authCache.timestamp < 2000)) {
      console.log('Using cached auth state');
      setAuthState({
        isAuthenticated: true,
        userId,
        isLoading: false
      });
      return;
    }
    
    // Skip if we're already verifying
    if (verifyingRef.current) {
      console.log('Auth verification already in progress, skipping');
      return;
    }
    
    // Skip if we've verified recently (reduced cooldown)
    if (now - lastVerificationTime < VERIFICATION_COOLDOWN) {
      console.log('Auth verification on cooldown, skipping');
      return;
    }
    
    // Start verification
    verifyingRef.current = true;
    lastVerificationTime = now;
    
    // Only set loading state if we don't have a token
    if (!token) {
      setAuthState(prev => ({ ...prev, isLoading: true }));
    }
    
    try {
      console.log('Checking auth status');
      // Check if currently authenticated
      const authenticated = isAuthenticated();
      
      // If not authenticated, try to refresh once
      let refreshed = false;
      if (!authenticated) {
        const newToken = await refreshToken();
        refreshed = !!newToken;
      }
      
      // Get the final authentication state
      const finalAuthState = authenticated || refreshed;
      const finalUserId = finalAuthState ? getUserId() : null;
      
      // Update cache
      authCache = {
        isAuthenticated: finalAuthState,
        userId: finalUserId,
        timestamp: now
      };
      
      setAuthState({
        isAuthenticated: finalAuthState,
        userId: finalUserId,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error checking authentication status:', error);
      setAuthState({
        isAuthenticated: false,
        userId: null,
        isLoading: false,
      });
    } finally {
      verifyingRef.current = false;
    }
  }, []);

  // Fast initial check on mount
  useEffect(() => {
    const token = getAccessToken();
    const userId = getUserId();
    
    // If we have a token, assume authenticated initially
    if (token && userId) {
      console.log('Initial token found, assuming authenticated');
      setAuthState({
        isAuthenticated: true,
        userId,
        isLoading: false
      });
      
      // Update cache
      authCache = {
        isAuthenticated: true,
        userId,
        timestamp: Date.now()
      };
    }
    
    // Then do a full check in the background
    checkAuthStatus();
    
    // Listen for token changes
    const handleTokenChange = () => {
      checkAuthStatus();
    };
    
    // Listen for auth expiration
    const handleAuthExpired = () => {
      authCache = {
        isAuthenticated: false,
        userId: null,
        timestamp: Date.now()
      };
      
      setAuthState({
        isAuthenticated: false,
        userId: null,
        isLoading: false,
      });
    };
    
    // Listen for direct auth state changes from AuthContext
    const handleAuthStateChange = (event: CustomEvent) => {
      if (event.detail && typeof event.detail.isAuthenticated === 'boolean') {
        console.log('Auth state change event received:', event.detail);
        
        // Update cache
        authCache = {
          isAuthenticated: event.detail.isAuthenticated,
          userId: event.detail.userId || null,
          timestamp: Date.now()
        };
        
        setAuthState({
          isAuthenticated: event.detail.isAuthenticated,
          userId: event.detail.userId || null,
          isLoading: false,
        });
      }
    };
    
    window.addEventListener('tokenChange', handleTokenChange);
    window.addEventListener('authExpired', handleAuthExpired);
    window.addEventListener('authStateChange', handleAuthStateChange as EventListener);
    
    return () => {
      window.removeEventListener('tokenChange', handleTokenChange);
      window.removeEventListener('authExpired', handleAuthExpired);
      window.removeEventListener('authStateChange', handleAuthStateChange as EventListener);
    };
  }, [checkAuthStatus]);

  return {
    ...authState,
    refresh: checkAuthStatus,
  };
};

export default useAuth;
